import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHero } from "../components/site/Bits";
import ProductCard from "../components/site/ProductCard";
import { CATEGORIES } from "../components/site/data";
import { api } from "../lib/api";

export default function Catalogue() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/public/products").then((r) => r.data) });
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.filter((p) => (cat === "All" || p.category === cat) && (!s || `${p.name} ${p.summary} ${p.category}`.toLowerCase().includes(s)));
  }, [data, cat, q]);

  return (
    <>
      <Seo title="Assistive Technology Equipment Catalogue" path="/equipment"
        description="Browse mobility, bathroom, bedroom, seating, daily living and paediatric equipment available for NDIS trials at home in Melbourne." />
      <PageHero eyebrow="Equipment catalogue" title="Find equipment to trial at home"
        intro="Search or filter by category, then request a trial. Not sure what you need? Request a trial anyway and describe the task you need help with." />
      <section className="container-x py-12" aria-label="Catalogue">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <label htmlFor="catalogue-search" className="font-semibold">Search equipment</label>
            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" size={20} aria-hidden="true" />
              <input id="catalogue-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="For example: shower chair"
                className="field-input !mt-0 pl-12" data-testid="catalogue-search" />
            </div>
          </div>
        </div>
        <fieldset className="mt-6">
          <legend className="font-semibold">Filter by category</legend>
          <div className="mt-3 flex flex-wrap gap-2">
            {["All", ...CATEGORIES.map((c) => c.name)].map((c) => (
              <button key={c} type="button" aria-pressed={cat === c} onClick={() => setCat(c)}
                className={`min-h-[48px] rounded-full border-2 px-5 font-semibold transition-colors ${cat === c ? "border-teal bg-teal text-on-teal" : "border-line bg-surface hover:border-teal"}`}
                data-testid={`catalogue-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>
            ))}
          </div>
        </fieldset>
        {cat !== "All" && <p className="mt-4 text-ink-muted">{CATEGORIES.find((c) => c.name === cat)?.desc}</p>}
        <p className="mt-8 font-semibold" role="status" aria-live="polite" data-testid="catalogue-count">
          {isLoading ? "Loading equipment…" : `${list.length} item${list.length === 1 ? "" : "s"} found`}
        </p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
        {!isLoading && list.length === 0 && (
          <p className="card mt-6 p-8 text-lg">No equipment matches your search. Try another word, or <a href="/request-trial" className="font-semibold text-teal underline">request a trial</a> and tell us what you need.</p>
        )}
      </section>
    </>
  );
}
